import { z } from "zod";
//#region src/backend/records/verification-token-record.ts
/**
* Every Verification_Purpose, for a sweep that has to cover all of them.
*
* Closure invalidates an account's unconsumed tokens whatever they were issued
* for, and the repository's filters name one purpose, so the sweep iterates.
*
* The `satisfies` clause refuses a member that is not a purpose; it cannot refuse
* an omission, so a purpose added to the union above is added here too. The sweep
* is asserted over this array rather than over a restated list, so a test names
* the omission if one is left.
*/
const VERIFICATION_PURPOSES = [
	"email-verification",
	"email-change",
	"password-reset"
];
//#endregion
//#region src/backend/api/request-schemas.ts
/**
* One Request_Schema per route of the capability's router.
*
* These are the authoritative field rules: the router validates against them,
* the OpenAPI document is generated from them, and the frontend derives its
* field validation from them. The module imports `zod`, four type-only contracts
* and one bound from the token record, and nothing else, so it remains safe to
* load in a browser bundle — the record module imports one type and reaches no
* Node built-in, which is why the bound is declared there rather than in
* `VerificationService`.
*
* `strictObject` rather than `object` throughout. An unknown property is
* rejected outright rather than silently stripped, so the parsed value carries
* exactly the declared fields and nothing undeclared can reach the
* `AuthenticationService`. It also makes the generated schema carry
* `additionalProperties: false` in both input and output modes.
*
* Every field below is one of the five declarations that follow the bounds. A
* route that judges an address, a password or a token judges it by the rule the
* flow it serves applies, from one declaration rather than a restatement beside
* it (R15.4).
*/
/** The longest address RFC 5321 permits. */
const maximumEmailLength = 254;
/** Matches `MINIMUM_PASSWORD_LENGTH` in `AuthenticationService`. */
const minimumPasswordLength = 12;
/**
* An upper bound rather than a policy: it keeps a pathological input away from
* the password hasher, which is deliberately slow.
*/
const maximumPasswordLength = 256;
/**
* An address an account may sign in with: the rule registration applies, and the
* rule an email change applies to the address it asks for (R8.3).
*
* The email is checked with `check(z.email())` rather than the deprecated
* `.email()`, and after `trim`, so a padded address is judged on its trimmed
* form rather than rejected for the padding.
*/
const registeredEmailField = z.string().trim().min(1).max(maximumEmailLength).check(z.email());
/**
* An address presented to be looked up rather than to be registered: bounded and
* trimmed, with no format rule.
*
* Deliberately weaker than {@link registeredEmailField}. An address registered
* before the current format rule must still reach the service, which answers a
* login with the single shared failure message and a reset request with the same
* nothing it answers an unregistered address with (R9.2). A route that rejected
* it at the transport would refuse a person the flow written for them.
*/
const presentedEmailField = z.string().trim().min(1).max(maximumEmailLength);
/**
* A password presented to prove who is acting — at a login, and at each act
* requiring Reauthentication.
*
* Bounded and never judged against the length policy, for the reason
* {@link presentedEmailField} carries no format rule: the stored password may
* predate the current minimum, and a person holding one must still be able to
* sign in, change it or close the account. The service answers a mismatch; the
* schema's business is the bound alone.
*/
const presentedPasswordField = z.string().min(1).max(maximumPasswordLength);
/**
* A password being stored: registration's rules, applied wherever a password is
* set (R7.3, R9.8).
*
* The minimum is `assertValidPassword`'s, which the service applies to every one
* of these flows regardless of the route taken; the maximum is this module's, for
* the reason the bound exists at all.
*/
const storedPasswordField = z.string().min(minimumPasswordLength).max(maximumPasswordLength);
/**
* A Verification_Token presented for consumption, bounded by length (R5.10).
*
* The bound is `MAXIMUM_VERIFICATION_TOKEN_LENGTH`, which `VerificationService`
* refuses a longer token by, so the schema turns a pathological body away at the
* transport and the service covers the callers that reach it without passing
* through a route — one declaration holding both paths.
*
* Not trimmed: a token is compared against a stored digest, so the value the
* schema yields is the value that was presented.
*/
const verificationTokenField = z.string().min(1).max(128);
/** `POST /register`. */
const registrationRequestSchema = z.strictObject({
	email: registeredEmailField,
	password: storedPasswordField
});
/**
* `POST /login`.
*
* Deliberately weaker than registration: no address format and no password
* length policy, only bounds. A credential that no longer satisfies the current
* registration rules must still be able to reach the service, which answers
* with the single shared failure message rather than revealing that the address
* is unknown.
*/
const loginRequestSchema = z.strictObject({
	email: presentedEmailField,
	password: presentedPasswordField
});
/** `POST /refresh`. The credential travels in the cookie, so the body carries nothing. */
const refreshRequestSchema = z.strictObject({});
/** `POST /logout`. The credential travels in the cookie, so the body carries nothing. */
const logoutRequestSchema = z.strictObject({});
/** `GET /me`. The access token travels in the `Authorization` header. */
const currentUserRequestSchema = z.strictObject({});
/**
* `POST /email/verify`. Unauthenticated: the token is the credential, and a person
* following a link from an inbox holds no session.
*/
const verifyEmailRequestSchema = z.strictObject({ token: verificationTokenField });
/**
* `POST /email/verify/resend`. The account is the one its access token names, so
* there is no address in the body to point at another's.
*/
const resendVerificationRequestSchema = z.strictObject({});
/**
* `POST /password/change`.
*
* The current password is part of the body rather than implied by the access token
* that authorised the request: the act requires Reauthentication (R7.1). The two
* fields are judged by different rules for that reason — the current one by the
* bound alone, since it is only being compared with what is stored, and the new one
* by registration's rules, since it is what will be stored.
*/
const changePasswordRequestSchema = z.strictObject({
	currentPassword: presentedPasswordField,
	newPassword: storedPasswordField
});
/**
* `POST /password/reset`. Unauthenticated, and it names an address rather than an
* account: a person who cannot remember their password cannot be signed in.
*/
const requestPasswordResetRequestSchema = z.strictObject({ email: presentedEmailField });
/**
* `POST /password/reset/confirm`. Unauthenticated, the token naming the account
* whose password is being replaced.
*/
const completePasswordResetRequestSchema = z.strictObject({
	token: verificationTokenField,
	newPassword: storedPasswordField
});
/**
* `POST /email/change`.
*
* `newEmail` rather than `email`, because the account already has one and this is
* not it, and it is judged by what registration judges an address by (R8.3): the
* address asked for here becomes a Login_Identifier once proven.
*/
const requestEmailChangeRequestSchema = z.strictObject({
	newEmail: registeredEmailField,
	currentPassword: presentedPasswordField
});
/**
* `POST /email/change/confirm`.
*
* Unauthenticated, and deliberately: the token is the credential, the pending
* change already recorded which account it belongs to, and requiring a session
* would mean a person who requested the change on a laptop could not confirm it
* from a phone.
*/
const confirmEmailChangeRequestSchema = z.strictObject({ token: verificationTokenField });
/**
* `GET /email/change`. The pending change of the account the access token names,
* so there is nothing in the body that could name another's.
*/
const pendingEmailChangeRequestSchema = z.strictObject({});
/** `DELETE /email/change`. The account is the one its access token names. */
const withdrawEmailChangeRequestSchema = z.strictObject({});
/** `GET /sessions`. The account is the one its access token names. */
const listSessionsRequestSchema = z.strictObject({});
/**
* `DELETE /sessions/{sessionId}`. The session is named by the path rather than by
* the body, which carries nothing.
*/
const revokeSessionRequestSchema = z.strictObject({});
/**
* `DELETE /sessions`. Revokes every session but the Current_Session, which the
* access token names, so the body says nothing about which one is spared.
*/
const revokeOtherSessionsRequestSchema = z.strictObject({});
/**
* `POST /account/close`.
*
* The password is required here, as R12.1 requires it of the route. Closing without
* one is exposed to a caller acting outside HTTP (R12.10) and to no request, so the
* field is not optional and the body carries no way to omit it.
*/
const closeAccountRequestSchema = z.strictObject({ currentPassword: presentedPasswordField });
/**
* A human label for a credential, as an operator sets it to tell one key from
* another on an account (R1, R7.3). Bounded, trimmed; the service stores it.
*/
const credentialLabelField = z.string().trim().min(1).max(100);
/**
* The public handle a ceremony issues and a client echoes to name the challenge
* it is completing (not the challenge itself — a UUID). Bounded as the ids this
* capability mints are.
*/
const challengeIdField = z.string().min(1).max(100);
/**
* The authenticator's credential id, base64url, as an assertion presents it. The
* handle the stored credential is found by; bounded well above a real one.
*/
const credentialIdField = z.string().min(1).max(256);
/**
* An opaque authenticator response — the attestation or assertion object the
* browser's `@simplewebauthn/browser` produced.
*
* `z.looseObject({})` rather than a described shape: the structure is WebAuthn's
* and the verifier reads it behind the `WebAuthnVerifier` seam, so no library type
* crosses into this schema (the capability's seam rule). The router hands it to the
* service as `unknown`; the generated document describes it as a free-form object,
* which is what the browser SDK's output is. A non-object body is still refused.
*/
const authenticatorResponseField = z.looseObject({});
/**
* The single-use continuation handle a `required`-mode login issues (R6.3). An
* opaque base64url token; bounded as a verification token is, for the same reason.
*/
const continuationTokenField = z.string().min(1).max(128);
/**
* `POST /webauthn/registration/begin`. Bearer: the account registering is the one
* the access token names, or — administratively — the one the path names (R1.6,
* R5.5). The body carries only the label for the new key.
*/
const beginRegistrationRequestSchema = z.strictObject({ label: credentialLabelField });
/**
* `POST /webauthn/registration/complete`. The challenge handle, the authenticator's
* attestation, and the label the ceremony began with.
*/
const completeRegistrationRequestSchema = z.strictObject({
	challengeId: challengeIdField,
	response: authenticatorResponseField,
	label: credentialLabelField
});
/**
* `POST /webauthn/assertion/begin`. Unauthenticated: the passwordless login's
* ceremony names no account, the authenticator choosing the discoverable
* credential (R2.1). The body carries nothing.
*/
const beginAssertionRequestSchema = z.strictObject({});
/**
* `POST /webauthn/assertion/complete`. The passwordless login (R2.2): the challenge
* handle, the credential the authenticator asserted, and the assertion response.
*/
const completeAssertionRequestSchema = z.strictObject({
	challengeId: challengeIdField,
	credentialId: credentialIdField,
	response: authenticatorResponseField
});
/**
* `POST /login/second-factor`. The `required`-mode continuation step (R6.3): the
* continuation handle and the ceremony completion, which is an assertion or an
* enrolment depending on the step the login demanded.
*
* A discriminated union on `step`: `assert` carries an assertion, `enrol` carries a
* registration response and label. The router dispatches on it.
*/
const secondFactorStepRequestSchema = z.discriminatedUnion("step", [z.strictObject({
	continuationToken: continuationTokenField,
	step: z.literal("assert"),
	challengeId: challengeIdField,
	credentialId: credentialIdField,
	response: authenticatorResponseField
}), z.strictObject({
	continuationToken: continuationTokenField,
	step: z.literal("enrol"),
	challengeId: challengeIdField,
	response: authenticatorResponseField
})]);
/**
* `POST /webauthn/enrolment/begin`. Unauthenticated: the Admin_Bootstrap's first-key
* enrolment (R8.1). The body carries the one-time enrolment ticket alone — the account
* is the ticket's, not a session's, because a bootstrap account has no session until a
* key is enrolled. The ticket is bounded as a continuation token is, for the same
* reason: it is an opaque base64url handle a person pastes from the bootstrap output.
*/
const beginEnrolmentRequestSchema = z.strictObject({ ticket: continuationTokenField });
/**
* `POST /webauthn/enrolment/complete`. The challenge handle, the authenticator's
* attestation and the label, over the same enrolment ticket the begin step peeked —
* which this step spends (R8.1). Unauthenticated for the same reason: the ticket is
* the credential.
*/
const completeEnrolmentRequestSchema = z.strictObject({
	ticket: continuationTokenField,
	challengeId: challengeIdField,
	response: authenticatorResponseField,
	label: credentialLabelField
});
/**
* `POST /admin/accounts`. Administrative account creation (R5.1): the address of the
* passwordless account to provision. Bearer, gated by `ACCOUNT_CREATE_ANY`.
*/
const createAccountRequestSchema = z.strictObject({ email: registeredEmailField });
/** `POST /admin/accounts/{userId}/disable`. The account is the path's; the body is empty. */
const disableAccountRequestSchema = z.strictObject({});
/** `POST /admin/accounts/{userId}/enable`. The account is the path's; the body is empty. */
const enableAccountRequestSchema = z.strictObject({});
/** `GET /admin/accounts`. The page is a query parameter; the body carries nothing. */
const listAccountsRequestSchema = z.strictObject({});
/**
* The `page` query parameter of `GET /admin/accounts`: one-based, defaulting to 1.
*
* Declared once and shared by the route table (which publishes it) and the router
* (which parses `request.query.page` through it), so the bound the document states
* is the bound enforced — one declaration, as every field rule here is. `z.coerce`
* so a query string's `"2"` becomes `2`.
*/
const accountsPageQuerySchema = z.coerce.number().int().min(1).default(1);
/**
* The `pageSize` query parameter of `GET /admin/accounts`: 1–100, defaulting to 25.
* Shared between the route table and the router as {@link accountsPageQuerySchema}
* is.
*/
const accountsPageSizeQuerySchema = z.coerce.number().int().min(1).max(100).default(25);
/** `GET /admin/accounts/{userId}/credentials`. The account is the path's; the body is empty. */
const listCredentialsRequestSchema = z.strictObject({});
/** `DELETE /credentials/{credentialId}`. The credential is the path's; the body is empty. */
const disableCredentialRequestSchema = z.strictObject({});
//#endregion
export { revokeSessionRequestSchema as A, pendingEmailChangeRequestSchema as C, requestPasswordResetRequestSchema as D, requestEmailChangeRequestSchema as E, verifyEmailRequestSchema as M, withdrawEmailChangeRequestSchema as N, resendVerificationRequestSchema as O, VERIFICATION_PURPOSES as P, logoutRequestSchema as S, registrationRequestSchema as T, enableAccountRequestSchema as _, beginRegistrationRequestSchema as a, listSessionsRequestSchema as b, completeAssertionRequestSchema as c, completeRegistrationRequestSchema as d, confirmEmailChangeRequestSchema as f, disableCredentialRequestSchema as g, disableAccountRequestSchema as h, beginEnrolmentRequestSchema as i, secondFactorStepRequestSchema as j, revokeOtherSessionsRequestSchema as k, completeEnrolmentRequestSchema as l, currentUserRequestSchema as m, accountsPageSizeQuerySchema as n, changePasswordRequestSchema as o, createAccountRequestSchema as p, beginAssertionRequestSchema as r, closeAccountRequestSchema as s, accountsPageQuerySchema as t, completePasswordResetRequestSchema as u, listAccountsRequestSchema as v, refreshRequestSchema as w, loginRequestSchema as x, listCredentialsRequestSchema as y };

//# sourceMappingURL=request-schemas-DKZGbaal.mjs.map